import { downloadText } from '../engine/views/csv';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, type Client } from '../engine/api/client';
import { Icon } from '../engine/design/icons';
import { Button } from '../engine/design/primitives/Button';
import { Badge, EmptyState, Skeleton, type Tone } from '../engine/design/primitives/Misc';
import type { IconName } from '../engine/design/icons';

// Small building blocks shared by the portal pages (list chrome, tones, errors). Pure
// presentation over the design system — no data access here.

export function BackLink({ to, children = 'Back' }: { to: string; children?: ReactNode }) {
  return <Link to={to} className="pt-back"><Icon name="arrow-left" size={14} />{children}</Link>;
}

export function ViewToggle({ value, onChange }: { value: 'cards' | 'table'; onChange: (v: 'cards' | 'table') => void }) {
  return (
    <div className="pt-viewtoggle" role="group" aria-label="View">
      <button type="button" aria-pressed={value === 'cards'} onClick={() => onChange('cards')}><Icon name="layout-dashboard" size={14} />Cards</button>
      <button type="button" aria-pressed={value === 'table'} onClick={() => onChange('table')}><Icon name="table" size={14} />Table</button>
    </div>
  );
}

/** Remembers the customer's card/table preference per list (a per-device convenience). */
export function useViewPref(key: string): ['cards' | 'table', (v: 'cards' | 'table') => void] {
  const k = `hf.portal.view.${key}`;
  const [v, setV] = useState<'cards' | 'table'>(() => {
    try {
      return localStorage.getItem(k) === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });
  return [
    v,
    (next) => {
      setV(next);
      try {
        localStorage.setItem(k, next);
      } catch {
        /* ignore */
      }
    },
  ];
}

export function LoadMore({ hasMore, loading, onMore }: { hasMore: boolean; loading: boolean; onMore: () => void }) {
  if (!hasMore) return null;
  return <div className="pt-more"><Button variant="outline" busy={loading} onClick={onMore}>Show more</Button></div>;
}

export function ListState({ loading, error, empty, count, children }: { loading: boolean; error: unknown; empty: { title: string; hint?: ReactNode; icon?: IconName; action?: ReactNode }; count: number; children: ReactNode }) {
  if (loading) return <Skeleton lines={5} />;
  if (error) return <ErrorState error={error} />;
  if (count === 0) return <EmptyState title={empty.title} hint={empty.hint} icon={empty.icon ?? 'inbox'} action={empty.action} />;
  return <>{children}</>;
}

export function ErrorState({ error, action }: { error: unknown; action?: ReactNode }) {
  const e = error instanceof ApiError ? error : null;
  // 402: the account's plan does not include it; 403: switched off for the account, or not in the role.
  const title = e?.status === 404 ? 'Not found' : e?.status === 402 ? 'Not included in your plan' : e?.status === 403 ? 'Not available to you' : 'Something went wrong';
  return <EmptyState title={title} hint={e?.detail ?? (error instanceof Error ? error.message : String(error))} icon={e?.status === 403 || e?.status === 402 ? 'lock' : 'alert-circle'} action={action} />;
}

const TICKET_TONES: Record<string, Tone> = { new: 'info', open: 'accent', pending: 'warn', resolved: 'pos', closed: 'muted', cancelled: 'muted', in_progress: 'accent' };
const DOC_TONES: Record<string, Tone> = { sent: 'info', viewed: 'info', accepted: 'pos', converted: 'pos', declined: 'neg', expired: 'muted', revised: 'muted', issued: 'info', frozen: 'info', paid: 'pos', overdue: 'neg', pending: 'warn', approved: 'pos', rejected: 'neg', timed_out: 'muted', cancelled: 'muted', active: 'pos', done: 'pos', in_progress: 'accent', todo: 'muted', blocked: 'neg', on_hold: 'warn' };

export function StatusPill({ status, label, kind = 'doc' }: { status: string | null | undefined; label?: string | null; kind?: 'ticket' | 'doc' }) {
  if (!status) return null;
  const tone = (kind === 'ticket' ? TICKET_TONES : DOC_TONES)[status] ?? 'muted';
  return <Badge tone={tone}>{label ?? status.replace(/_/g, ' ')}</Badge>;
}

export function Section({ title, extra, children }: { title: ReactNode; extra?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h2 className="hf-section-title hf-between">{title}{extra}</h2>
      {children}
    </section>
  );
}

/** Bytes as a short human label (attachments). */
export function fmtBytes(n: number | string): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(0)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Open a portal document (an invoice or quote print view, or a connected system's PDF) in a new tab.
 * A plain link cannot carry the portal's bearer token, so the document is fetched with it: an HTML
 * view opens from a blob URL; a `{ url }` answer (a short-lived signed PDF link) opens that link.
 * The tab is opened first, synchronously, so a popup blocker allows it.
 */
export async function openDocument(api: Client, path: string): Promise<void> {
  const win = window.open('', '_blank');
  try {
    const text = await api.request<string>(path, { as: 'text' });
    let url: string | null = null;
    if (text.trimStart().startsWith('{')) {
      const parsed = JSON.parse(text) as { url?: unknown };
      if (typeof parsed.url === 'string' && /^https:\/\//.test(parsed.url)) url = parsed.url;
    }
    // A blob document runs in the portal's origin: forbid scripts in it (the view is server-escaped HTML; this is defence in depth).
    const target = url ?? URL.createObjectURL(new Blob([`<meta http-equiv="Content-Security-Policy" content="script-src 'none'; object-src 'none'; base-uri 'none'">${text}`], { type: 'text/html' }));
    if (win) win.location.href = target;
    else window.location.assign(target);
  } catch (e) {
    win?.close();
    throw e;
  }
}

/** Save a file the portal must fetch with its bearer (e.g. an appointment's .ics). */
export async function downloadFile(api: Client, path: string, filename: string, mime: string): Promise<void> {
  downloadText(filename, await api.request<string>(path, { as: 'text' }), mime, false);
}
